import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { widgetApi } from './widgetApi';
import type { WidgetConfig, WidgetConfigPatch, WidgetSnippet } from '../model/types';

export const widgetQueryKeys = {
  all: ['widget-config'] as const,
  config: (channelId: string) => [...widgetQueryKeys.all, 'config', channelId] as const,
  snippet: (channelId: string) => [...widgetQueryKeys.all, 'snippet', channelId] as const,
};

export function useWidgetConfig(channelId: string | undefined) {
  return useQuery<WidgetConfig, Error>({
    queryKey: widgetQueryKeys.config(channelId ?? ''),
    queryFn: () => widgetApi.getConfig(channelId!),
    enabled: !!channelId,
    staleTime: 10_000,
  });
}

/**
 * PATCH config — обновление настроек виджета.
 *
 * onSuccess: инвалидируем config (свежие данные с бэка с пересчитанными
 * contrast-цветами) и snippet (на всякий случай — он не должен меняться
 * от patch, но если бэк-логика изменится — будем безопасны).
 */
export function usePatchWidgetConfigMutation(channelId: string) {
  const qc = useQueryClient();
  return useMutation<WidgetConfig, Error, WidgetConfigPatch>({
    mutationFn: (payload) => widgetApi.patchConfig(channelId, payload),
    onSuccess: (data) => {
      qc.setQueryData(widgetQueryKeys.config(channelId), data);
    },
  });
}

export function useWidgetSnippet(channelId: string | undefined) {
  return useQuery<WidgetSnippet, Error>({
    queryKey: widgetQueryKeys.snippet(channelId ?? ''),
    queryFn: () => widgetApi.getSnippet(channelId!),
    enabled: !!channelId,
    staleTime: Infinity, // снипет не меняется
  });
}
